// Password validation utility
export interface PasswordValidationResult {
	isValid: boolean;
	errors: string[];
	strength: "weak" | "medium" | "strong";
}

export const validatePassword = (password: string): PasswordValidationResult => {
	const errors: string[] = [];

	// Check minimum length
	if (password.length < 8) {
		errors.push("Password must be at least 8 characters long");
	}

	// Check for uppercase letter
	if (!/[A-Z]/.test(password)) {
		errors.push("Password must contain at least 1 uppercase letter");
	}

	// Check for lowercase letter
	if (!/[a-z]/.test(password)) {
		errors.push("Password must contain at least 1 lowercase letter");
	}

	// Check for number
	if (!/\d/.test(password)) {
		errors.push("Password must contain at least 1 number");
	}

	// Determine strength
	let strength: "weak" | "medium" | "strong" = "weak";
	if (errors.length === 0) {
		if (password.length >= 12 && /[!@#$%^&*(),.?":{}|<>]/.test(password)) {
			strength = "strong";
		} else if (password.length >= 10) {
			strength = "medium";
		} else {
			strength = "strong"; // Meets basic requirements
		}
	}

	return {
		isValid: errors.length === 0,
		errors,
		strength,
	};
};

export interface PasswordRequirement {
	label: string;
	/** How the rule reads in "Password needs …" when it isn't met. */
	missingText: string;
	valid: boolean;
}

// Same four rules as validatePassword and the backend's UserService.validatePassword.
export const getPasswordRequirements = (password: string): PasswordRequirement[] => [
	{ label: "At least 8 characters", missingText: "at least 8 characters", valid: password.length >= 8 },
	{ label: "At least 1 uppercase letter", missingText: "an uppercase letter", valid: /[A-Z]/.test(password) },
	{ label: "At least 1 lowercase letter", missingText: "a lowercase letter", valid: /[a-z]/.test(password) },
	{ label: "At least 1 number", missingText: "a number", valid: /\d/.test(password) },
];

/** "Password needs an uppercase letter and a number." or null when every rule is met. */
export const describeMissingPasswordRequirements = (password: string): string | null => {
	const missing = getPasswordRequirements(password)
		.filter((requirement) => !requirement.valid)
		.map((requirement) => requirement.missingText);
	if (missing.length === 0) return null;
	const list =
		missing.length === 1 ? missing[0] : `${missing.slice(0, -1).join(", ")} and ${missing[missing.length - 1]}`;
	return `Password needs ${list}.`;
};

// Password strength indicator component props
export interface PasswordStrengthIndicatorProps {
	password: string;
	showStrength?: boolean;
}
